import type { BaseballStats, PlayerListItem } from "../shared/types/contracts";
import type { PlayoffSeriesModel } from "../shared/types/contracts";
import {
  calculateExportRates,
  emptyBaseballStats,
  STAT_COLUMNS,
  type ArchivedGameStats,
} from "./baseballStats";
import type { BattingPlayer } from "./gamePresentation";

export type WorkbookCell = string | number;
export type WorkbookRow = Record<string, WorkbookCell>;

export interface StatsWorkbookRows {
  currentGame: WorkbookRow[];
  gameLog: WorkbookRow[];
  regularSeasonTotals: WorkbookRow[];
  playoffTotals: WorkbookRow[];
}

export interface StatsWorkbookInput {
  battingList: BattingPlayer[];
  statsByPlayerId: Record<string, BaseballStats>;
  gameArchive: ArchivedGameStats[];
  activePlayers: PlayerListItem[];
  playoffSeries?: PlayoffSeriesModel[];
  currentGamePlayoffSeriesId?: string | null;
}

function statColumns(stats: BaseballStats): WorkbookRow {
  return Object.fromEntries(
    STAT_COLUMNS.map((column) => [column, stats[column]]),
  );
}

export function buildStatsWorkbookRows({
  battingList,
  statsByPlayerId,
  gameArchive,
  activePlayers,
  playoffSeries = [],
  currentGamePlayoffSeriesId = null,
}: StatsWorkbookInput): StatsWorkbookRows {
  const currentGame = battingList.map((player, index) => {
    const stats =
      (player.id ? statsByPlayerId[player.id] : undefined) ||
      emptyBaseballStats();
    return {
      Order: index + 1,
      Player: player.name,
      ...statColumns(stats),
      ...calculateExportRates(stats),
    };
  });

  const gameLog: WorkbookRow[] = [];
  const seriesById = new Map(
    playoffSeries.map((series) => [series.id, series]),
  );
  gameArchive.forEach((game, gameIndex) => {
    const series = game.playoffSeriesId
      ? seriesById.get(game.playoffSeriesId)
      : null;
    Object.entries(game.stats).forEach(([name, stats]) => {
      gameLog.push({
        Game: gameIndex + 1,
        Date: game.date,
        Competition: game.playoffSeriesId ? "Playoffs" : "Regular Season",
        Series: series?.title ?? (game.playoffSeriesId ? "Playoffs" : ""),
        "Series Game": game.seriesGameNumber ?? "",
        Opponent: game.opponent,
        Result: `${game.ourScore}-${game.oppScore}`,
        Player: name,
        ...Object.fromEntries(
          STAT_COLUMNS.map((column) => [column, stats[column] ?? 0]),
        ),
      });
    });
  });

  const regularTotals: Record<string, BaseballStats & { GP: number }> = {};
  const playoffTotals: Record<string, BaseballStats & { GP: number }> = {};
  const addToTotals = (
    totals: Record<string, BaseballStats & { GP: number }>,
    name: string,
    stats: BaseballStats,
    isForfeit = false,
  ) => {
    const playerTotals = totals[name] ?? {
      GP: 0,
      ...emptyBaseballStats(),
    };
    if (!isForfeit) playerTotals.GP += 1;
    STAT_COLUMNS.forEach((column) => {
      playerTotals[column] += stats[column] ?? 0;
    });
    totals[name] = playerTotals;
  };

  gameArchive.forEach((game) => {
    const totals = game.playoffSeriesId ? playoffTotals : regularTotals;
    Object.entries(game.stats).forEach(([name, stats]) =>
      addToTotals(totals, name, stats, Boolean(game.forfeitWinner)),
    );
  });
  activePlayers.forEach((player) => {
    const stats = statsByPlayerId[player.id];
    if (stats) {
      addToTotals(
        currentGamePlayoffSeriesId ? playoffTotals : regularTotals,
        player.name,
        stats,
      );
    }
  });

  const totalRows = (totals: Record<string, BaseballStats & { GP: number }>) =>
    Object.entries(totals)
      .map(([name, stats]) => ({
        Player: name,
        GP: stats.GP,
        ...statColumns(stats),
        ...calculateExportRates(stats),
      }))
      .sort((a, b) => b.AVG - a.AVG);
  const regularSeasonTotals = totalRows(regularTotals);
  const combinedPlayoffTotals = totalRows(playoffTotals);

  return {
    currentGame,
    gameLog: gameLog.length ? gameLog : [{ Note: "No archived games yet" }],
    regularSeasonTotals: regularSeasonTotals.length
      ? regularSeasonTotals
      : [{ Note: "No regular-season stats yet" }],
    playoffTotals: combinedPlayoffTotals.length
      ? combinedPlayoffTotals
      : [{ Note: "No stats yet" }],
  };
}

export function buildStatsWorkbookFilename(
  teamName: string,
  isoDate: string,
): string {
  return `${teamName.replace(/\s+/g, "_")}_stats_${isoDate}.xlsx`;
}
